import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  UsersIcon, 
  EyeIcon, 
  DocumentArrowDownIcon,
  ClockIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import UserAttributionModal from '@/components/admin/UserAttributionModal'
import { authFetch } from '@/utils/api'

interface ActivityItem {
  id: string
  activity_type: 'pageview' | 'interaction' | 'download'
  user_id: string
  ip_address: string
  session_id: string
  timestamp: string
  page_url?: string
  element_id?: string
  document_title?: string
  [key: string]: any
}

interface DashboardStats {
  connectedUsers: number
  pageViews: number
  interactions: number
  uniqueUsers: number
  uniqueIPs: number
}

interface SessionData {
  id: string
  ip_address?: string
  ip?: string
  session_id: string
  first_seen: string
  last_seen: string
  total_requests: number
  page_count: number
  interaction_count: number
  session_duration: number
}

const DashboardPage = () => {
  const [liveData, setLiveData] = useState<{
    liveActivities: ActivityItem[]
    summary: DashboardStats
    timestamp: string
    trackingMode?: string
    dataSource?: string
  } | null>(null)
  
  const [sessions, setSessions] = useState<SessionData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshInterval, setRefreshInterval] = useState(5000) // 5 seconds
  const [timeRange, setTimeRange] = useState(30) // 30 minutes
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [showAttributionModal, setShowAttributionModal] = useState(false)
  const [liveUsers, setLiveUsers] = useState<any[]>([])
  const [liveUsersLoading] = useState(false)

  // Fetch live activity data
  const fetchLiveData = async () => {
    try {
      const response = await authFetch(`/api/monitoring/live?minutes=${timeRange}&limit=50`)
      if (!response.ok) throw new Error('Failed to fetch live data')
      const result = await response.json()
      
      if (result.success) {
        console.log(`🔍 Tracking Mode: ${result.data.trackingMode || 'Unknown'}`)
        console.log(`📊 Data Source: ${result.data.dataSource || 'Unknown'}`)
        
        setLiveData({
          liveActivities: result.data.liveActivities || result.data.activities || [],
          summary: result.data.summary || {
            uniqueUsers: 0,
            pageViews: 0,
            interactions: 0,
            uniqueIPs: 0,
            connectedUsers: 0
          },
          timestamp: result.data.timestamp,
          trackingMode: result.data.trackingMode,
          dataSource: result.data.dataSource
        })
        
        // Update live users from the response
        if (result.data.liveUsers) {
          setLiveUsers(result.data.liveUsers)
        }
      }
    } catch (err) {
      console.error('Error fetching real tracking data:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    }
  }

  // Fetch session data
  const fetchSessions = async () => {
    try {
      const response = await authFetch('/api/monitoring/sessions?hours=24&limit=20')
      if (!response.ok) throw new Error('Failed to fetch sessions')
      const result = await response.json()
      
      if (result.success) {
        setSessions(result.data.sessions || [])
      }
    } catch (err) {
      console.error('Error fetching sessions:', err)
    }
  }

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchLiveData(), fetchSessions(), fetchLiveUsers()])
      setLoading(false)
    }
    
    loadData()
  }, [timeRange])

  // Set up refresh interval
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLiveData()
      fetchSessions()
      fetchLiveUsers()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [refreshInterval, timeRange])

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${Math.round(seconds / 3600)}h`
  }

  const getActivityIcon = (type: string) => {
    switch (type || 'unknown') {
      case 'pageview':
      case 'visited': return <EyeIcon className="h-4 w-4" />
      case 'interaction': return <ComputerDesktopIcon className="h-4 w-4" />
      case 'download': return <DocumentArrowDownIcon className="h-4 w-4" />
      default: return <ClockIcon className="h-4 w-4" />
    }
  }

  const getActivityColor = (type: string) => {
    switch (type || 'unknown') {
      case 'pageview':
      case 'visited': return 'text-blue-600 bg-blue-50'
      case 'interaction': return 'text-green-600 bg-green-50'
      case 'download': return 'text-purple-600 bg-purple-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const getBotProbabilityColor = (probability: number) => {
    if (probability >= 0.8) return 'text-red-600 bg-red-50'
    if (probability >= 0.5) return 'text-orange-600 bg-orange-50'
    if (probability >= 0.3) return 'text-yellow-600 bg-yellow-50'
    return 'text-green-600 bg-green-50'
  }

  const getBotProbabilityLabel = (probability: number) => {
    if (probability >= 0.8) return 'Very High'
    if (probability >= 0.5) return 'High'
    if (probability >= 0.3) return 'Medium'
    return 'Low'
  }

  const handleViewAttribution = (sessionId: string) => {
    setSelectedSessionId(sessionId)
    setShowAttributionModal(true)
  }

  const closeAttributionModal = () => {
    setShowAttributionModal(false)
    setSelectedSessionId(null)
  }

  // Fetch live users data (now included in the main live data)
  const fetchLiveUsers = async () => {
    // Live users are now fetched as part of the main live data
    // This function is kept for compatibility but doesn't need to do anything
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading monitoring dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Monitoring Dashboard - CyberScrape Training Platform</title>
        <meta name="description" content="Real-time monitoring dashboard for instructor oversight of student activities" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Real Tracking Status Banner */}
        {liveData?.trackingMode === 'REAL_DATA' && (
          <div className="bg-green-50 border-b border-green-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    🔍 REAL USER TRACKING ACTIVE
                  </h3>
                  <div className="text-xs text-green-700">
                    All data shown below is from actual user interactions, browser fingerprinting, and real-time behavior analysis.
                    <span className="ml-2 font-semibold">Source:</span> {liveData.dataSource || 'Live user tracking'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Monitoring Dashboard</h1>
                <p className="text-sm text-gray-600">
                  {liveData?.trackingMode === 'REAL_DATA' ? 
                    'Real-time activity monitoring from actual users' : 
                    'Real-time activity monitoring for CyberScrape Training Platform'
                  }
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Time Range Selector */}
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(parseInt(e.target.value))}
                  className="form-input text-sm"
                >
                  <option value={15}>Last 15 minutes</option>
                  <option value={30}>Last 30 minutes</option>
                  <option value={60}>Last hour</option>
                  <option value={180}>Last 3 hours</option>
                </select>

                {/* Refresh Interval */}
                <select
                  value={refreshInterval}
                  onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                  className="form-input text-sm"
                >
                  <option value={1000}>1 second</option>
                  <option value={5000}>5 seconds</option>
                  <option value={10000}>10 seconds</option>
                  <option value={30000}>30 seconds</option>
                </select>

                {/* Status Indicator */}
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Stats Overview */}
          {liveData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow p-6"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UsersIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Users</p>
                    <p className="text-2xl font-bold text-gray-900">{liveData.summary.uniqueUsers}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg shadow p-6"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <EyeIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Page Views</p>
                    <p className="text-2xl font-bold text-gray-900">{liveData.summary.pageViews}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-lg shadow p-6"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <ComputerDesktopIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Interactions</p>
                    <p className="text-2xl font-bold text-gray-900">{liveData.summary.interactions}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-lg shadow p-6"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <GlobeAltIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Unique IPs</p>
                    <p className="text-2xl font-bold text-gray-900">{liveData.summary.uniqueIPs}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-lg shadow p-6"
              >
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ChartBarIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Activity</p>
                    <p className="text-2xl font-bold text-gray-900">{liveData.liveActivities.length}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Live Activity Feed */}
            <div className="xl:col-span-2 bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Live Activity Feed</h2>
                <p className="text-sm text-gray-600">
                  Real-time user activities (last {timeRange} minutes)
                </p>
              </div>
              
              <div className="p-6">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {liveData?.liveActivities.map((activity, index) => (
                    <motion.div
                      key={`${activity.id}-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className={`p-2 rounded-full ${getActivityColor(activity.activity_type || activity.action)}`}>
                        {getActivityIcon(activity.activity_type || activity.action)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {(activity.activity_type || activity.action) === 'pageview' || activity.action === 'visited' ? (activity.page_url || activity.target || 'Page view') : ''}
                              {(activity.activity_type || activity.action) === 'interaction' ? `${activity.element_type || 'Element'}: ${activity.element_id || 'Unknown'}` : ''}
                              {(activity.activity_type || activity.action) === 'download' ? (activity.document_title || activity.target || 'Download') : ''}
                              {activity.action && !['pageview', 'visited', 'interaction', 'download'].includes(activity.action) ? `${activity.action}: ${activity.target || 'Unknown'}` : ''}
                            </p>
                            {activity.bot_indicators && activity.bot_indicators.length > 0 && (
                              <div className="mt-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  🤖 Bot Detected
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {new Date(activity.timestamp).toLocaleTimeString()}
                            </p>
                            {activity.geolocation && (
                              <p className="text-xs text-gray-400">
                                {activity.geolocation.city}, {activity.geolocation.country}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-1 text-xs text-gray-600 space-y-1">
                          <div className="flex items-center justify-between">
                            <p>
                              IP: {activity.ip || activity.ip_address || 'Unknown'} | Session: {(activity.session_id || activity.id || 'unknown').toString().slice(-8)}
                            </p>
                            <button
                              onClick={() => handleViewAttribution(activity.session_id || activity.id || 'unknown')}
                              className="ml-2 inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <MagnifyingGlassIcon className="h-3 w-3 mr-1" />
                              View Details
                            </button>
                          </div>
                          {activity.user_agent && (
                            <p className="truncate">
                              <span className="font-medium">UA:</span> {activity.user_agent}
                            </p>
                          )}
                          {activity.referrer && (
                            <p className="truncate">
                              <span className="font-medium">Referrer:</span> {activity.referrer}
                            </p>
                          )}
                          {activity.viewport && (
                            <p>
                              <span className="font-medium">Viewport:</span> {activity.viewport} | 
                              <span className="font-medium"> Lang:</span> {activity.language || 'N/A'} | 
                              <span className="font-medium"> TZ:</span> {activity.timezone || 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  
                  {(!liveData?.liveActivities || liveData.liveActivities.length === 0) && (
                    <div className="text-center py-8">
                      <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No recent activity</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Active Sessions */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Active Sessions</h2>
                <p className="text-sm text-gray-600">
                  Recent user sessions (last 24 hours)
                </p>
              </div>
              
              <div className="p-6">
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {sessions.map((session, index) => (
                    <motion.div
                      key={session.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${
                            new Date(session.last_seen).getTime() > Date.now() - 30 * 60 * 1000 
                              ? 'bg-green-500' 
                              : 'bg-gray-400'
                          }`}></div>
                          <span className="font-medium text-gray-900">
                            {session.ip_address || session.ip || 'Unknown IP'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDuration(session.session_duration || 0)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Pages</p>
                          <p className="font-medium">{session.page_count || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Interactions</p>
                          <p className="font-medium">{session.interaction_count || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Requests</p>
                          <p className="font-medium">{session.total_requests}</p>
                        </div>
                      </div>
                      
                      <div className="mt-2 text-xs text-gray-500">
                        Session: {(session.session_id || session.id || 'unknown').toString().slice(-12)}
                      </div>
                    </motion.div>
                  ))}
                  
                  {sessions.length === 0 && (
                    <div className="text-center py-8">
                      <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No active sessions</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Live Users Panel */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <UsersIcon className="h-5 w-5 mr-2 text-green-600" />
                  Live Users
                </h2>
                <p className="text-sm text-gray-600">
                  Currently active users with detailed attribution
                </p>
              </div>
              
              <div className="p-6">
                {liveUsersLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading live users...</p>
                  </div>
                ) : liveUsers.length > 0 ? (
                  <div className="space-y-4">
                    {liveUsers.map((user, index) => (
                      <motion.div
                        key={user.session_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <div className={`w-3 h-3 rounded-full mr-2 ${
                              user.bot_probability > 0.5 ? 'bg-red-500' : 'bg-green-500'
                            } animate-pulse`}></div>
                            <span className="font-medium text-gray-900">
                              {user.session_id.slice(-8)}
                            </span>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${getBotProbabilityColor(user.bot_probability)}`}>
                            {getBotProbabilityLabel(user.bot_probability)} Risk
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Current Page</p>
                            <p className="font-medium text-gray-900 truncate">{user.current_page}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Time on Page</p>
                            <p className="font-medium text-gray-900">{user.time_on_page}s</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Location</p>
                            <p className="font-medium text-gray-900">
                              {user.location.city}, {user.location.country}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">IP Address</p>
                            <p className="font-medium text-gray-900">{user.ip_address}</p>
                          </div>
                        </div>
                        
                        <div className="mt-3 text-xs text-gray-600">
                          <p className="truncate">
                            <span className="font-medium">UA:</span> {user.user_agent}
                          </p>
                          {user.bot_indicators && user.bot_indicators.length > 0 && (
                            <div className="mt-2">
                              <p className="font-medium text-red-600 mb-1">Bot Indicators:</p>
                              <div className="flex flex-wrap gap-1">
                                {user.bot_indicators.map((indicator: string, idx: number) => (
                                  <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    {indicator}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3 flex justify-between items-center">
                          <div className="text-xs text-gray-500">
                            Pages: {user.activity_summary.pages_viewed} | 
                            Interactions: {user.activity_summary.interactions} | 
                            Active: {formatDuration(user.activity_summary.time_active)}
                          </div>
                          <button
                            onClick={() => handleViewAttribution(user.session_id)}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <MagnifyingGlassIcon className="h-3 w-3 mr-1" />
                            Analyze
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No live users detected</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Attribution Modal */}
      {selectedSessionId && (
        <UserAttributionModal
          isOpen={showAttributionModal}
          onClose={closeAttributionModal}
          sessionId={selectedSessionId}
        />
      )}
    </>
  )
}

export default DashboardPage
